import {
  List, Datagrid, TextField, BooleanField, NumberField, EditButton,
  Edit, Create, SimpleForm, TextInput, BooleanInput, NumberInput,
  SelectInput, ReferenceInput, AutocompleteInput, FunctionField,
  useGetList, useRecordContext,
} from 'react-admin';
import { Stack, Box, Chip, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

const LOCATIONS = [
  { id: 'header',           name: 'Header — main nav' },
  { id: 'topbar_right',     name: 'Header — topbar (logins)' },
  { id: 'footer_quick',     name: 'Footer — Quick Menu' },
  { id: 'footer_resources', name: 'Footer — Resources' },
  { id: 'footer_bottom',    name: 'Footer — bottom row' },
];

const LOCATION_LABEL = Object.fromEntries(LOCATIONS.map((l) => [l.id, l.name]));

const COLOR_BY_LOCATION = {
  header:           { bg: 'rgba(29,77,58,.10)',  fg: 'var(--green-700, #1d4d3a)' },
  topbar_right:     { bg: 'rgba(154,120,24,.12)', fg: 'var(--gold-700, #9a7818)' },
  footer_quick:     { bg: 'rgba(63,128,100,.10)', fg: 'var(--green, #25634a)' },
  footer_resources: { bg: 'rgba(63,128,100,.10)', fg: 'var(--green, #25634a)' },
  footer_bottom:    { bg: 'rgba(93,102,96,.12)',  fg: 'var(--ink-3, #5d6660)' },
};

function LocationChip() {
  const record = useRecordContext();
  if (!record) return null;
  const palette = COLOR_BY_LOCATION[record.location] || COLOR_BY_LOCATION.footer_bottom;
  return (
    <Chip
      size="small"
      label={LOCATION_LABEL[record.location] || record.location}
      sx={{
        background: palette.bg,
        color: palette.fg,
        fontWeight: 600,
        fontSize: 11,
        height: 22,
      }}
    />
  );
}

function ParentChip() {
  const record = useRecordContext();
  if (!record?.parentId) return <span style={{ color: '#999' }}>—</span>;
  return <Chip size="small" label={`# ${record.parentId}`} sx={{ fontSize: 11, height: 22 }} />;
}

const filters = [
  <SelectInput key="location" source="location" choices={LOCATIONS} alwaysOn />,
  <TextInput key="q" source="q" label="Search label/url" alwaysOn />,
];

const MenuItemList = () => (
  <List
    sort={{ field: 'sortOrder', order: 'ASC' }}
    filters={filters}
    perPage={50}
    pagination={false}
  >
    <Datagrid rowClick="edit" bulkActionButtons={false}>
      <FunctionField label="Location" render={() => <LocationChip />} />
      <TextField source="label" />
      <TextField source="url" />
      <FunctionField label="Parent" render={() => <ParentChip />} />
      <BooleanField source="openInNewTab" label="New tab" />
      <BooleanField source="isVisible" label="Visible" />
      <NumberField source="sortOrder" label="Order" />
      <EditButton />
    </Datagrid>
  </List>
);

/**
 * Parent selector — only meaningful for `header` items. Filtered to
 * top-level header rows (parentId NULL) so admins can't nest 3 levels deep.
 */
function ParentSelector() {
  const record = useRecordContext();
  const location = record?.location;
  // Only fetch when needed
  const { data: candidates, isLoading } = useGetList(
    'menu-items',
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: 'sortOrder', order: 'ASC' },
      filter: { location: 'header' },
    },
    { enabled: location === 'header' },
  );

  if (location !== 'header') return null;
  if (isLoading) return null;

  // Only top-level header items can be parents; exclude the row being edited
  const choices = (candidates || [])
    .filter((c) => !c.parentId && c.id !== record?.id)
    .map((c) => ({ id: c.id, name: c.label }));

  return (
    <SelectInput
      source="parentId"
      choices={choices}
      emptyText="— Top level —"
      parse={(v) => (v === '' ? null : v)}
      label="Parent menu item (header dropdowns only)"
      helperText="Leave blank for a top-level menu item. Choosing a parent makes this a sublink inside that menu."
    />
  );
}

const MenuItemForm = () => (
  <Stack spacing={2} sx={{ maxWidth: 720 }}>
    <SelectInput source="location" choices={LOCATIONS} defaultValue="header" />

    <ParentSelector />

    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <TextInput source="label" fullWidth />
      <NumberInput source="sortOrder" defaultValue={0} sx={{ width: 140 }} />
    </Stack>

    <TextInput
      source="url"
      fullWidth
      defaultValue="#"
      helperText='Internal: "/about" or "/page/scholarship". External: full https URL. Use "#" for a pure dropdown trigger.'
    />

    <TextInput
      source="sectionTitle"
      label="Dropdown section title (header parents only)"
      fullWidth
      helperText='Optional. Shows as a small heading above the dropdown items. E.g. "Levels & programmes".'
    />

    <Stack direction="row" spacing={2}>
      <BooleanInput source="openInNewTab" label="Open in a new tab" />
      <BooleanInput source="isVisible" label="Visible on the site" defaultValue={true} />
    </Stack>

    <Box sx={{ background: 'var(--cream, #faf6ec)', border: '1px dashed var(--line, #e6e1d4)', borderRadius: 1, p: 1.5, mt: 1 }}>
      <Typography variant="caption" sx={{ display: 'block', color: 'var(--ink-3, #5d6660)' }}>
        <strong>Tip:</strong> The Header supports 2 levels — top-level items and sublinks inside them. All other locations (footer / topbar) are flat. Reorder within a location by changing <code>sortOrder</code> (smaller = earlier).
      </Typography>
    </Box>
  </Stack>
);

const MenuItemEdit = () => (
  <Edit redirect="list" mutationMode="pessimistic">
    <SimpleForm><MenuItemForm /></SimpleForm>
  </Edit>
);

const MenuItemCreate = () => (
  <Create redirect="list">
    <SimpleForm><MenuItemForm /></SimpleForm>
  </Create>
);

export default {
  list: MenuItemList,
  edit: MenuItemEdit,
  create: MenuItemCreate,
  icon: MenuIcon,
  options: { label: 'Menus & links' },
  recordRepresentation: (r) => `${r.label} (${r.location})`,
};
